import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, map, mergeMap, of, switchMap} from 'rxjs';
import {RouterSelectors} from '@store/aviary/aviary.adapter';
import {whenRouterHasNavigatedTo, withLatestFromSelectors} from '@store/common/effects.helpers';
import {PatronSelectors} from '@store/patron/patron.selectors';
import {BalanceActions} from './balance.actions';
import {BalanceService} from './balance.service';
import {BalanceSelectors} from './balance.selectors';
import {MakeAPaymentValue, SquareAppDetails} from './balance.types';
import {MakeAPaymentDialog} from '@app/my-account/balance/dialogs/make-a-payment.dialog';
import {PaymentConfirmationDialog} from '@app/my-account/balance/dialogs/payment-confirmation.dialog';
import {openNotificationDialog, openPersistentFormDialog} from '@aviary/store/common/effect-dialog.helpers';

@Injectable({providedIn: 'root'})
export class BalanceEffects {

  private readonly actions: Actions = inject(Actions);
  private readonly service: BalanceService = inject(BalanceService);

  public loadBalanceInfoAndTransactionsOnRouterNavigated = createEffect(() =>
    this.actions.pipe(
      whenRouterHasNavigatedTo('BALANCE'),
      mergeMap(() => [
        BalanceActions.loadBalanceTransactions(),
        BalanceActions.loadSquareAppDetails()
      ])
    )
  );

  public loadBalanceTransactions = createEffect(() =>
    this.actions.pipe(
      ofType(BalanceActions.loadBalanceTransactions, BalanceActions.submitMakeAPaymentSuccess),
      withLatestFromSelectors(
        RouterSelectors.queryParams,
        BalanceSelectors.BalanceTransactions.includeAllTransactions
      ),
      switchMap(([, queryParams, includeAllTransactions]) => this.service.loadBalanceTransactions(queryParams, includeAllTransactions).pipe(
        map(response => BalanceActions.loadBalanceTransactionsSuccess(response)),
        catchError((error: unknown) => of(BalanceActions.loadBalanceTransactionsFailure(error)))
      ))
    )
  );

  public loadBalanceTransactionsOnToggleIncludeAll = createEffect(() =>
    this.actions.pipe(
      ofType(BalanceActions.toggleIncludeAllBalanceTransactions),
      map(() => BalanceActions.loadBalanceTransactions())
    )
  );

  public loadSquareAppDetails = createEffect(() =>
    this.actions.pipe(
      ofType(BalanceActions.loadSquareAppDetails),
      switchMap(() => this.service.loadSquareAppDetails().pipe(
        map((response: SquareAppDetails) => BalanceActions.loadSquareAppDetailsSuccess(response)),
        catchError((error: unknown) => of(BalanceActions.loadSquareAppDetailsFailure(error)))
      ))
    )
  );

  public showMakeAPayment = createEffect(() =>
    this.actions.pipe(
      ofType(BalanceActions.showMakeAPayment),
      withLatestFromSelectors(
        PatronSelectors.AccountStatus.currentAccountBalance,
        BalanceSelectors.SquareApp.details,
        BalanceSelectors.SquareApp.minimumAmount
      ),
      openPersistentFormDialog(([, currentAccountBalance, squareAppDetails, minimumAmount]) => ({
        componentClass: MakeAPaymentDialog,
        data: {squareAppDetails, currentAccountBalance, minimumAmount},
        onConfirm: (value: MakeAPaymentValue, dialog: MakeAPaymentDialog) => dialog.tokenizeCard().pipe(
          map(tokenValue => ({...value, ...tokenValue}))
        ),
        onSuccess: (value: MakeAPaymentValue) => BalanceActions.submitMakeAPayment(value)
      }))
    )
  );

  public submitMakeAPayment = createEffect(() =>
    this.actions.pipe(
      ofType(BalanceActions.submitMakeAPayment),
      switchMap(({value}) => this.service.submitMakeAPayment(value).pipe(
        mergeMap((transaction) => [
          BalanceActions.showPaymentConfirmation({...value, transactionId: String(transaction.id)}),
          BalanceActions.submitMakeAPaymentSuccess()
        ]),
        catchError((error: unknown) => of(BalanceActions.submitMakeAPaymentFailure(error)))
      ))
    )
  );

  public showMakeAPaymentForDependent = createEffect(() =>
    this.actions.pipe(
      ofType(BalanceActions.showMakeAPaymentForDependent),
      withLatestFromSelectors(
        RouterSelectors.paramValueAsNumberWithFallback('id', 0),
        PatronSelectors.DependentAccountStatus.currentAccountBalance,
        BalanceSelectors.SquareApp.details,
        BalanceSelectors.SquareApp.minimumAmount
      ),
      openPersistentFormDialog(([, dependentPatronId, currentAccountBalance, squareAppDetails, minimumAmount]) => ({
        componentClass: MakeAPaymentDialog,
        data: {squareAppDetails, currentAccountBalance, minimumAmount},
        onConfirm: (value: MakeAPaymentValue, dialog: MakeAPaymentDialog) => dialog.tokenizeCard().pipe(
          map(tokenValue => ({...value, ...tokenValue}))
        ),
        onSuccess: (value: MakeAPaymentValue) => BalanceActions.submitMakeAPaymentForDependent(value, dependentPatronId)
      }))
    )
  );

  public submitMakeAPaymentForDependent = createEffect(() =>
    this.actions.pipe(
      ofType(BalanceActions.submitMakeAPaymentForDependent),
      switchMap(({value, dependentPatronId}) => this.service.submitMakeAPaymentForDependent(value, dependentPatronId).pipe(
        mergeMap((transaction) => [
          BalanceActions.showPaymentConfirmation({...value, transactionId: String(transaction.id)}),
          BalanceActions.submitMakeAPaymentForDependentSuccess()
        ]),
        catchError((error: unknown) => of(BalanceActions.submitMakeAPaymentForDependentFailure(error)))
      ))
    )
  );

  public showPaymentConfirmation = createEffect(() =>
    this.actions.pipe(
      ofType(BalanceActions.showPaymentConfirmation),
      openNotificationDialog(({value}) => ({
        componentClass: PaymentConfirmationDialog,
        data: {value}
      }))
    )
  );
}
